import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Wrench, Building2, Search, X, Save, IndianRupee, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  gst_number: "", pan_number: "", proprietor_name: "",
  contact_person: "", contact_mobile: "", contact_email: "",
  bank_account_no: "", bank_ifsc: "",
  agreement_signed_at: "", agreement_valid_till: "",
  daily_capacity_vehicles: "", bays: "", tat_days_target: "",
  commission_pct: "", payment_terms_days: "",
  active: true, notes: "",
};

export default function WorkshopManagement() {
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // workshop_id
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workshops${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setList(r.data.workshops || []);
      setCount(r.data.count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load workshops");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (workshop_id) => {
    setSelected(workshop_id); setDetail(null);
    try {
      const r = await api.get(`/workshops/${workshop_id}`);
      setDetail(r.data);
      const p = r.data.profile || {};
      setForm({
        gst_number: p.gst_number || "", pan_number: p.pan_number || "",
        proprietor_name: p.proprietor_name || "",
        contact_person: p.contact_person || "", contact_mobile: p.contact_mobile || "",
        contact_email: p.contact_email || "",
        bank_account_no: p.bank_account_no || "", bank_ifsc: p.bank_ifsc || "",
        agreement_signed_at: p.agreement_signed_at || "", agreement_valid_till: p.agreement_valid_till || "",
        daily_capacity_vehicles: p.daily_capacity_vehicles ?? "", bays: p.bays ?? "",
        tat_days_target: p.tat_days_target ?? "",
        commission_pct: p.commission_pct ?? "", payment_terms_days: p.payment_terms_days ?? "",
        active: p.active !== false, notes: p.notes || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load workshop");
    }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const patch = { ...form };
      // coerce numerics
      ["daily_capacity_vehicles", "bays", "payment_terms_days"].forEach(k => {
        patch[k] = patch[k] === "" ? null : parseInt(patch[k], 10);
      });
      ["tat_days_target", "commission_pct"].forEach(k => {
        patch[k] = patch[k] === "" ? null : parseFloat(patch[k]);
      });
      await api.patch(`/workshops/${selected}`, patch);
      toast.success("Profile saved");
      await openDetail(selected);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950 py-8" data-testid="workshop-management-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-600 dark:text-orange-400 mb-1 flex items-center gap-2">
              <Building2 className="w-3 h-3" /> Vendor Network
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 dark:text-white">Workshop Management</h1>
            <p className="text-sm text-slate-500 mt-1">{count} workshops · GST, PAN, agreement, capacity, TAT, payments.</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   placeholder="Search name, city, GST, pincode…"
                   className="pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 w-72"
                   data-testid="workshop-search-input" />
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* LEFT: list */}
          <div className="lg:col-span-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 max-h-[70vh] overflow-y-auto" data-testid="workshop-list">
            {loading ? (
              <div className="p-6 text-slate-500 text-sm">Loading…</div>
            ) : list.map((w) => (
              <button key={w.workshop_id}
                      onClick={() => openDetail(w.workshop_id)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selected === w.workshop_id ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}
                      data-testid={`workshop-item-${w.workshop_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{w.name}</div>
                    <div className="text-xs text-slate-500 truncate">{w.city} · {w.pincode}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {w.profile_created ? (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">Profiled</span>
                    ) : (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">Seed</span>
                    )}
                    {w.gst_number && <span className="text-[9px] font-mono text-slate-500">{w.gst_number.slice(0, 6)}…</span>}
                  </div>
                </div>
              </button>
            ))}
            {!loading && list.length === 0 && (
              <div className="p-6 text-slate-400 text-sm">No workshops match your search.</div>
            )}
          </div>

          {/* RIGHT: detail / edit */}
          <div className="lg:col-span-3">
            {!selected ? (
              <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-10 text-center text-slate-500 text-sm">
                <Wrench className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                Select a workshop on the left to view or edit its profile.
              </div>
            ) : !detail ? (
              <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-10 text-center text-slate-500 text-sm">Loading…</div>
            ) : (
              <div className="space-y-4">
                {/* Header */}
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="workshop-detail-header">
                  <div className="min-w-0">
                    <div className="font-heading font-black text-xl text-slate-900 dark:text-white truncate">{detail.profile.name}</div>
                    <div className="text-xs text-slate-500">{detail.profile.address} · {detail.profile.city} · {detail.profile.pincode}</div>
                    <div className="font-mono text-[10px] text-slate-400 mt-1">{detail.profile.workshop_id}</div>
                  </div>
                  <button onClick={() => { setSelected(null); setDetail(null); }} className="text-slate-500 hover:text-slate-800 dark:hover:text-white" data-testid="workshop-detail-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Performance */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <PerfCard label="Rating" value={detail.performance.rating_avg ? `★ ${detail.performance.rating_avg}` : "—"} sub={`${detail.performance.rating_count} reviews`} icon={Star} testid="perf-rating" />
                  <PerfCard label="Active claims" value={detail.performance.active_claims} sub={`${detail.performance.total_claims} total`} testid="perf-active" />
                  <PerfCard label="TAT actual" value={detail.performance.tat_actual_days ? `${detail.performance.tat_actual_days}d` : "—"} sub={detail.profile.tat_days_target ? `target ${detail.profile.tat_days_target}d` : "no target"} testid="perf-tat" />
                  <PerfCard label="YTD paid" value={`₹${(detail.performance.ytd_payments_inr || 0).toLocaleString("en-IN")}`} icon={IndianRupee} testid="perf-paid" />
                </div>

                {/* Edit form */}
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="workshop-edit-form">
                  <div className="font-heading font-bold text-slate-900 dark:text-white mb-4">Profile & Agreement</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="GST Number" testid="fld-gst"      value={form.gst_number}      onChange={(v) => setForm(f => ({ ...f, gst_number: v }))} />
                    <Field label="PAN"        testid="fld-pan"      value={form.pan_number}      onChange={(v) => setForm(f => ({ ...f, pan_number: v }))} />
                    <Field label="Proprietor" testid="fld-prop"     value={form.proprietor_name} onChange={(v) => setForm(f => ({ ...f, proprietor_name: v }))} />
                    <Field label="Contact person" testid="fld-cp"    value={form.contact_person}  onChange={(v) => setForm(f => ({ ...f, contact_person: v }))} />
                    <Field label="Contact mobile" testid="fld-cm"    value={form.contact_mobile}  onChange={(v) => setForm(f => ({ ...f, contact_mobile: v }))} />
                    <Field label="Contact email"  testid="fld-ce"    value={form.contact_email}   onChange={(v) => setForm(f => ({ ...f, contact_email: v }))} />
                    <Field label="Bank A/c"       testid="fld-bank"  value={form.bank_account_no} onChange={(v) => setForm(f => ({ ...f, bank_account_no: v }))} />
                    <Field label="IFSC"           testid="fld-ifsc"  value={form.bank_ifsc}       onChange={(v) => setForm(f => ({ ...f, bank_ifsc: v }))} />
                    <Field label="Agreement signed on" type="date" testid="fld-signed" value={form.agreement_signed_at?.slice(0, 10)} onChange={(v) => setForm(f => ({ ...f, agreement_signed_at: v }))} />
                    <Field label="Agreement valid till" type="date" testid="fld-valid"  value={form.agreement_valid_till?.slice(0, 10)} onChange={(v) => setForm(f => ({ ...f, agreement_valid_till: v }))} />
                    <Field label="Daily capacity (vehicles)" testid="fld-cap"  type="number" value={form.daily_capacity_vehicles} onChange={(v) => setForm(f => ({ ...f, daily_capacity_vehicles: v }))} />
                    <Field label="Bays"                       testid="fld-bays" type="number" value={form.bays} onChange={(v) => setForm(f => ({ ...f, bays: v }))} />
                    <Field label="TAT target (days)"          testid="fld-tat"  type="number" value={form.tat_days_target} onChange={(v) => setForm(f => ({ ...f, tat_days_target: v }))} />
                    <Field label="Commission %"               testid="fld-comm" type="number" value={form.commission_pct} onChange={(v) => setForm(f => ({ ...f, commission_pct: v }))} />
                    <Field label="Payment terms (days)"       testid="fld-terms" type="number" value={form.payment_terms_days} onChange={(v) => setForm(f => ({ ...f, payment_terms_days: v }))} />
                    <div className="flex items-center gap-2 mt-1">
                      <input type="checkbox" checked={!!form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="fld-active" />
                      <span className="text-sm text-slate-700 dark:text-slate-200">Active partner</span>
                    </div>
                  </div>
                  <textarea placeholder="Internal notes"
                            value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                            className="mt-3 w-full border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-950 min-h-[70px]"
                            data-testid="fld-notes" />
                  <div className="mt-4 flex justify-end">
                    <button onClick={save} disabled={saving} className="btn-signature rounded-lg px-5 py-2 text-sm font-semibold flex items-center gap-2" data-testid="workshop-save-btn">
                      <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500 mb-1">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-950" />
    </label>
  );
}

function PerfCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="font-heading font-black text-lg text-slate-900 dark:text-white mt-1 truncate">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
